import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useAdminStore } from '@/stores/admin'

/**
 * Authentication guard for protected worker routes
 *
 * Ensures that the user has a valid worker profile before accessing protected routes.
 * - Loads the user profile from storage if not already loaded
 * - Redirects to ProfileAssignment page if user is not authenticated
 * - Allows navigation to proceed if user is authenticated
 *
 * @param _to - The target route being navigated to
 * @param _from - The current route being navigated from
 * @param next - Function to resolve the navigation
 * @returns Promise that resolves when guard completes
 */
export async function authGuard(
    _to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext
): Promise<void> {
    const userStore = useUserStore()

    // Load profile if not already loaded
    if (!userStore.profile) {
        await userStore.loadProfile()
    }

    // Redirect to profile assignment if not authenticated
    if (!userStore.isAuthenticated) {
        console.warn('User not configured - redirecting to profile assignment')
        next({ name: 'ProfileAssignment' })
        return
    }

    next()
}

/**
 * Admin authentication guard for admin-only routes
 *
 * Protects admin routes by checking:
 * 1. Admin panel has been unlocked via the 5-tap gesture
 * 2. The admin panel access flow will handle password authentication
 *
 * Note: Password authentication is handled within the AdminPanel component itself.
 * This guard only checks that the gesture has been completed to allow navigation.
 *
 * @param _to - The target route being navigated to
 * @param _from - The current route being navigated from
 * @param next - Function to resolve the navigation
 */
export function adminGuard(
    _to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext
): void {
    const adminStore = useAdminStore()

    // Only check if the gesture has been completed
    // Password authentication will be handled by AdminPanel
    if (!adminStore.isAdminUnlocked) {
        console.warn('Admin panel not unlocked - redirecting to dashboard')
        next({ name: 'Dashboard' })
        return
    }

    next()
}
